import axios from "axios";
import { apiURL } from "../config/config";

const getAuthConfig = (config = {}) => {
  const token = localStorage.getItem("token");

  return {
    ...config,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(config.headers || {}),
    },
  };
};

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.projects)) return payload.projects;
  if (Array.isArray(payload?.entries)) return payload.entries;
  if (Array.isArray(payload?.submissions)) return payload.submissions;
  if (Array.isArray(payload?.approvals)) return payload.approvals;
  return [];
};

export const listTimesheetClients = async () => {
  const response = await axios.get(`${apiURL}clients`, getAuthConfig());
  return normalizeArray(response.data);
};

export const listTimesheetProjects = async (clientId) => {
  const response = await axios.get(`${apiURL}timesheets/projects`, {
    ...getAuthConfig(),
    params: { clientId },
  });

  return normalizeArray(response.data);
};

export const createTimesheetProject = async (payload) => {
  const response = await axios.post(
    `${apiURL}timesheets/projects`,
    payload,
    getAuthConfig()
  );
  return response.data;
};

export const updateTimesheetProject = async (id, payload) => {
  const response = await axios.patch(
    `${apiURL}timesheets/projects/${id}`,
    payload,
    getAuthConfig()
  );
  return response.data;
};

export const archiveTimesheetProject = async (id) => {
  const response = await axios.delete(
    `${apiURL}timesheets/projects/${id}`,
    getAuthConfig()
  );
  return response.data;
};

export const listTimesheetEntries = async ({
  from,
  to,
  clientId = "",
  projectId = "",
}) => {
  const response = await axios.get(`${apiURL}timesheets/entries`, {
    ...getAuthConfig(),
    params: {
      from,
      to,
      ...(clientId ? { clientId } : {}),
      ...(projectId ? { projectId } : {}),
    },
  });

  return normalizeArray(response.data);
};

export const createTimesheetEntry = async (payload) => {
  const response = await axios.post(
    `${apiURL}timesheets/entries`,
    payload,
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const updateTimesheetEntry = async (id, payload) => {
  const response = await axios.patch(
    `${apiURL}timesheets/entries/${id}`,
    payload,
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const deleteTimesheetEntry = async (id) => {
  const response = await axios.delete(
    `${apiURL}timesheets/entries/${id}`,
    getAuthConfig()
  );
  return response.data;
};

export const getTimesheetSummary = async ({ from, to }) => {
  const response = await axios.get(`${apiURL}timesheets/summary`, {
    ...getAuthConfig(),
    params: { from, to },
  });

  return response.data;
};

export const saveTimesheetEntriesBatch = async ({
  entries = [],
  deleteEntryIds = [],
}) => {
  const response = await axios.post(
    `${apiURL}timesheets/entries/batch`,
    { entries, deleteEntryIds },
    getAuthConfig()
  );

  return response.data;
};

export const fetchTimesheetOverview = async ({ from, to }) => {
  const response = await axios.get(`${apiURL}timesheets/overview`, {
    ...getAuthConfig(),
    params: { from, to },
  });

  return response.data?.data ?? response.data;
};

export const fetchTimesheetCapabilities = async () => {
  const response = await axios.get(
    `${apiURL}timesheets/capabilities`,
    getAuthConfig()
  );

  return {
    canViewDashboard: Boolean(response.data?.canViewDashboard),
    canApproveTimesheet: Boolean(response.data?.canApproveTimesheet),
  };
};

export const fetchTimesheetPeriodStatus = async (periodStart) => {
  const response = await axios.get(`${apiURL}timesheets/period-status`, {
    ...getAuthConfig(),
    params: { periodStart },
  });

  return response.data?.data ?? response.data;
};

export const reopenTimesheetPeriod = async ({
  userId,
  periodStart,
  reopenUntil,
  reason,
}) => {
  const response = await axios.post(
    `${apiURL}timesheets/admin/reopen`,
    { userId, periodStart, reopenUntil, reason },
    getAuthConfig()
  );

  return response.data?.data ?? response.data;
};

export const fetchTimesheetReopenStatus = async ({ userId, periodStart }) => {
  const response = await axios.get(`${apiURL}timesheets/admin/reopen-status`, {
    ...getAuthConfig(),
    params: { userId, periodStart },
  });

  return response.data?.data ?? response.data;
};

export const fetchTimesheetDashboardUsers = async () => {
  const response = await axios.get(
    `${apiURL}timesheets/dashboard/users`,
    getAuthConfig()
  );

  return {
    canViewDashboard: Boolean(response.data?.canViewDashboard),
    scope: response.data?.scope || "self",
    data: normalizeArray(response.data),
  };
};

export const fetchTimesheetDashboardSummary = async ({
  from,
  to,
  userId = "",
  clientId = "",
  projectId = "",
} = {}) => {
  const response = await axios.get(`${apiURL}timesheets/dashboard/summary`, {
    ...getAuthConfig(),
    params: {
      from,
      to,
      ...(userId ? { userId } : {}),
      ...(clientId ? { clientId } : {}),
      ...(projectId ? { projectId } : {}),
    },
  });

  return response.data;
};

export const fetchTimesheetDashboardExportData = async ({
  from,
  to,
  userId = "",
  clientId = "",
  projectId = "",
  department = "",
  team = "",
  search = "",
} = {}) => {
  const response = await axios.get(
    `${apiURL}timesheets/dashboard/export-data`,
    {
      ...getAuthConfig(),
      params: {
        from,
        to,
        ...(userId ? { userId } : {}),
        ...(clientId ? { clientId } : {}),
        ...(projectId ? { projectId } : {}),
        ...(department ? { department } : {}),
        ...(team ? { team } : {}),
        ...(search ? { search } : {}),
      },
    }
  );

  return response.data;
};

export const fetchEmployeeTimesheetSummary = async (userId, { from, to }) => {
  const response = await axios.get(
    `${apiURL}timesheets/dashboard/users/${userId}/summary`,
    {
      ...getAuthConfig(),
      params: { from, to },
    }
  );

  return response.data;
};

export const submitTimesheetWeek = async (periodStart) => {
  const response = await axios.post(
    `${apiURL}timesheets/submissions`,
    { periodStart },
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const fetchMyTimesheetSubmissions = async (params = {}) => {
  const response = await axios.get(`${apiURL}timesheets/submissions`, {
    ...getAuthConfig(),
    params,
  });
  return normalizeArray(response.data);
};

export const fetchMyTimesheetSubmission = async (id) => {
  const response = await axios.get(
    `${apiURL}timesheets/submissions/${id}`,
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const withdrawTimesheetSubmission = async (id) => {
  const response = await axios.post(
    `${apiURL}timesheets/submissions/${id}/withdraw`,
    {},
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const fetchTimesheetApprovalInbox = async (params = {}) => {
  const response = await axios.get(`${apiURL}timesheets/approvals`, {
    ...getAuthConfig(),
    params,
  });
  return normalizeArray(response.data);
};

export const fetchTimesheetApprovalDetail = async (id) => {
  const response = await axios.get(
    `${apiURL}timesheets/approvals/${id}`,
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const approveTimesheetSubmission = async (id, comment = "") => {
  const response = await axios.post(
    `${apiURL}timesheets/submissions/${id}/approve`,
    comment.trim() ? { comment: comment.trim() } : {},
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const rejectTimesheetSubmission = async (id, reason) => {
  const response = await axios.post(
    `${apiURL}timesheets/submissions/${id}/reject`,
    { reason: reason.trim() },
    getAuthConfig()
  );
  return response.data?.data ?? response.data;
};

export const fetchApprovedTimesheetSubmissions = async ({
  status = "approved",
  userId = "",
  from = "",
  to = "",
  search = "",
  page = 1,
  limit = 20,
} = {}) => {
  const response = await axios.get(
    `${apiURL}timesheets/admin/approved-submissions`,
    {
      ...getAuthConfig(),
      params: {
        status,
        page,
        limit,
        ...(userId ? { userId } : {}),
        ...(from && to ? { from, to } : {}),
        ...(search ? { search } : {}),
      },
    }
  );

  return {
    data: normalizeArray(response.data),
    pagination: response.data?.pagination || {
      page,
      limit,
      total: 0,
      totalPages: 0,
    },
  };
};

export const unlockTimesheetSubmission = async (id, { reason, reopenUntil }) => {
  const response = await axios.post(
    `${apiURL}timesheets/admin/submissions/${id}/unlock`,
    { reason: reason.trim(), ...(reopenUntil ? { reopenUntil } : {}) },
    getAuthConfig()
  );
  // reopenUntil อยู่ที่ response.data โดยตรง จึงคืน body ทั้งก้อน
  return response.data;
};
